import React from 'react';
import chroma from 'chroma-js';
import { AnimatePresence, motion } from 'framer-motion';
import styled, { useTheme } from 'styled-components/macro';
import { IconChevron, IconWrapper } from '../../icons';
import { Paragraph, Text } from '../../typography';
import { SPACINGS } from '../../utils';

type AccordionButtonStyledProps = {
  selected: boolean;
  disabled?: boolean;
  primary: string;
};
const AccordionButtonStyled = styled.button<AccordionButtonStyledProps>`
  display: block;
  position: relative;
  width: 100%;
  border: 1px solid
    ${(p) => (p.selected ? p.primary : p.theme.colors.secondary90w)};
  background-color: ${(p) =>
    p.disabled
      ? p.theme.colors.secondary95w
      : p.selected
      ? p.theme.colors.primary98w
      : p.theme.colors.white};
  padding: ${SPACINGS[6]};
  border-radius: 10px;
  cursor: pointer;
  overflow: hidden;
  transition: background-color 0.2s, border-color 0.2s;

  &:hover,
  &:focus {
    outline: none;
    border: 1px solid
      ${(p) =>
        p.disabled
          ? p.theme.colors.secondary95w
          : p.selected
          ? p.theme.colors.primary
          : p.theme.colors.secondary90w};
  }

  .title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    z-index: 2;
  }

  .description-container {
    position: relative;
    z-index: 2;
  }

  .description {
    padding-top: ${SPACINGS[4]};
  }

  .image {
    z-index: 1;
    position: absolute;
    bottom: 0;
    right: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s;

    &.visible {
      opacity: 1;
    }
  }
`;

const variants = {
  open: { opacity: 1, height: 'auto' },
  closed: { opacity: 0, height: 0 },
};

type AccordionButtonProps = {
  title: string | React.ReactNode;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  description?: string | React.ReactNode;
  style?: React.CSSProperties;
  initialIsOpen?: boolean;
  primaryColor?: string;
  image?: string;
  imageStyle?: React.CSSProperties;
};
function AccordionButton(props: AccordionButtonProps) {
  const theme = useTheme();
  const {
    title,
    selected,
    onClick,
    disabled,
    description,
    initialIsOpen,
    style,
    image,
    imageStyle,
    primaryColor = theme.colors.primary,
  } = props;

  const [isOpen, setIsOpen] = React.useState(initialIsOpen);
  function handleChevronClick(e: React.MouseEvent<HTMLDivElement>) {
    setIsOpen((b) => !b);
    e.stopPropagation();
  }

  function handleClick() {
    if (!disabled) onClick();
  }

  return (
    <AccordionButtonStyled
      type="button"
      style={style}
      onClick={handleClick}
      primary={primaryColor}
      disabled={disabled}
      selected={selected}
    >
      <div className="title">
        <>
          {typeof title === 'string' ? (
            <Text margin="none">{title}</Text>
          ) : (
            title
          )}
        </>
        {description && (
          <IconWrapper
            style={{ minWidth: SPACINGS[8], minHeight: SPACINGS[8] }}
            type="action"
            onClick={handleChevronClick}
            backgroundColor={theme.colors.primary95w}
            hoverColor={theme.colors.primary90w}
          >
            <IconChevron direction={isOpen ? 'top' : 'bottom'} stroke={theme.colors.primary}/>
          </IconWrapper>
        )}
      </div>
      {description && (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="description-container"
              initial="closed"
              animate="open"
              exit="closed"
              variants={variants}
            >
              <div className="description">
                {typeof description === 'string' ? (
                  <Paragraph size="sm" margin="none">
                    {description}
                  </Paragraph>
                ) : (
                  description
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
      {image && (
        <img
          style={imageStyle}
          className={`image ${isOpen && 'visible'}`}
          alt="background"
          src={image}
        />
      )}
    </AccordionButtonStyled>
  );
}

export default AccordionButton;
